import colors from './log-colors.json';

/**
 * Client-only logger with session tracking and structured logging
 */
interface LoggerOptions {
  scope?: string;
  enabled?: boolean;
}

type LogLevel = 'debug' | 'info' | 'log';

interface MooNamespace {
  logLevel?: LogLevel;
  setLogLevel: (level: LogLevel) => void;
  enableDebugMode: () => void;
  disableDebugMode: () => void;
  enableAnalytics: () => void;
  disableAnalytics: () => void;
}

declare global {
  interface Window {
    moo?: MooNamespace;
  }
}

export class Logger {
  private readonly sessionId: string;
  private readonly scope: string | undefined;
  private readonly enabled: boolean;

  constructor(options: LoggerOptions = {}) {
    this.sessionId = this.generateSessionId();
    this.scope = options.scope;
    this.enabled = options.enabled ?? true;
  }

  /**
   * Get current log level from window.moo namespace
   */
  private getLogLevel(): LogLevel | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    // Try to read from window.moo.logLevel (synced from Redux)
    const logLevel = (window.moo as any)?.logLevel;
    return logLevel;
  }

  /**
   * Check if a log level should be logged based on current log level setting
   * Error and warn always log regardless of log level
   */
  private shouldLog(level: string): boolean {
    // Error and warn always log
    if (level === 'error' || level === 'warn') {
      return true;
    }

    const currentLogLevel = this.getLogLevel();

    // If no log level is set, default behavior (log everything)
    if (!currentLogLevel) {
      return true;
    }

    // Check if level should be logged based on current log level
    switch (currentLogLevel) {
      case 'debug':
        // debug, info, log, warn, error
        return ['debug', 'info', 'log', 'warn', 'error'].includes(level);
      case 'info':
        // info, log, warn, error (excludes debug)
        return ['info', 'log', 'warn', 'error'].includes(level);
      case 'log':
        // log, warn, error (excludes debug and info)
        return ['log', 'warn', 'error'].includes(level);
      default:
        return true;
    }
  }

  /**
   * Generate a unique session ID for correlation
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * Get the call site from the stack trace (skip logger class frames)
   */
  private getCallSite(): string | undefined {
    try {
      const stack = new Error().stack;
      if (!stack) {
        return undefined;
      }

      const stackLines = stack.split('\n');
      // Find the first frame that's not from this logger class
      // Stack: Error (0), getCallSite (1), logInternal (2), log/warn/error/info/debug (3)
      // The actual caller should be at index 4 or later
      for (let i = 4; i < stackLines.length; i++) {
        const line = stackLines[i].trim();
        // Skip frames from logger.class.ts
        if (!line.includes('logger.class.ts') && line.length > 0) {
          // Extract file and line number if available
          const match =
            line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/) ||
            line.match(/at\s+(.+?):(\d+):(\d+)/);
          if (match) {
            // Return formatted call site
            const filePath = match[2] || match[1];
            const fileName = filePath.split('/').pop() || filePath;
            const lineNumber = match[3] || match[2];
            return `${fileName}:${lineNumber}`;
          }
          return line;
        }
      }
    } catch {
      // Ignore errors in stack trace parsing
    }
    return undefined;
  }

  /**
   * Internal logging method that handles console logging with structured data
   */
  private logInternal(
    level: string,
    consoleMethod: (...args: unknown[]) => void,
    eventOrMessage: string,
    data?: Record<string, unknown>
  ): void {
    // Check if this log level should be logged
    const shouldLogResult = this.enabled && this.shouldLog(level);

    if (!shouldLogResult) {
      return;
    }

    const color = colors[level as keyof typeof colors].color;
    const style = `color: ${color}; font-weight: bold;`;
    const _scope = this.scope ? `[${this.scope}] ` : ' ';
    const callSite = this.getCallSite();
    const callSiteInfo = callSite ? ` %c@ ${callSite}` : '';
    const callSiteStyle = callSite
      ? 'color: gray; font-size: 0.9em; font-weight: normal;'
      : '';

    if (data !== undefined) {
      // Structured logging: include session ID and data
      consoleMethod(
        `%c[${level.toUpperCase()}]${_scope}${eventOrMessage}${callSiteInfo}`,
        style,
        callSiteStyle,
        {
          sessionId: this.sessionId,
          ...data,
        }
      );
    } else {
      // Simple message logging: include session ID
      consoleMethod(
        `%c[${level.toUpperCase()}]${_scope}${eventOrMessage}${callSiteInfo}`,
        style,
        callSiteStyle,
        {
          sessionId: this.sessionId,
        }
      );
    }
  }

  /**
   * Helper to normalize arguments for both legacy and structured logging
   */
  private normalizeLogArgs(...args: unknown[]): {
    message: string;
    data?: Record<string, unknown>;
  } {
    if (args.length === 0) {
      return { message: '' };
    }

    // Structured format: log('event', { data })
    if (
      args.length === 2 &&
      typeof args[0] === 'string' &&
      typeof args[1] === 'object' &&
      args[1] !== null &&
      !Array.isArray(args[1])
    ) {
      return { message: args[0], data: args[1] as Record<string, unknown> };
    }

    // Legacy format: log(...args) - join all args as message
    const message = args.map(String).join(' ');
    return { message };
  }

  /**
   * Get current session ID for correlation
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Log a message (only when enabled)
   * Supports both legacy format: log('message') and structured: log('event', { data })
   */
  log(...args: unknown[]): void {
    const { message, data } = this.normalizeLogArgs(...args);
    this.logInternal('log', console.log, message, data);
  }

  /**
   * Log a warning (only when enabled)
   */
  warn(...args: unknown[]): void {
    const { message, data } = this.normalizeLogArgs(...args);
    this.logInternal('warn', console.warn, message, data);
  }

  /**
   * Log an error (only when enabled)
   */
  error(...args: unknown[]): void {
    const { message, data } = this.normalizeLogArgs(...args);
    this.logInternal('error', console.error, message, data);
  }

  /**
   * Log an info message (only when enabled)
   */
  info(...args: unknown[]): void {
    const { message, data } = this.normalizeLogArgs(...args);
    this.logInternal('info', console.log, message, data);
  }

  /**
   * Log a debug message (only when enabled)
   * Uses console.debug to appear in browser's Verbose console tab
   */
  debug(...args: unknown[]): void {
    const { message, data } = this.normalizeLogArgs(...args);
    this.logInternal('debug', console.debug, message, data);
  }
}
